  // --- Excel Export with Styles ---

  import React, { useEffect, useState, useRef } from 'react';
import { storage } from '../../firebase';
import { ExcelIcon, GreenDeleteIcon } from "../../components/Icons";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

 
// Helper to delete file from Firebase Storage by URL
const deleteFileByUrl = async (url) => {
  if (!url) return;
  try {
    const baseUrl = 'https://firebasestorage.googleapis.com/v0/b/';
    const bucket = storage.app.options.storageBucket;
    const pathStart = url.indexOf(bucket) + bucket.length + 1;
    const pathEnd = url.indexOf('?');
    const filePath = decodeURIComponent(url.substring(pathStart, pathEnd));
    const fileRef = storageRef(storage, filePath);
    await deleteObject(fileRef);
  } catch (err) {
    // Optionally handle error
  }
};
import { Plus, Trash2, Download, Upload, X, PlusIcon, PlusCircleIcon, Pencil } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "../ui/button";
import { getTableStructure, saveTableStructure, getTableData, saveTableData } from './customTableFirebaseUtils';

import ConfirmationPopup from "../ConfirmationPopup";

const inputTypes = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Button' },
  { value: 'file', label: 'File Upload' },
  { value: 'button', label: 'Button' },
  { value: 'date', label: 'Date' },
];

export default function CustomizableExperiencedProfileTable() {
  const [structure, setStructure] = useState([]);
  const [data, setData] = useState([]);
  const [newCol, setNewCol] = useState({ name: '', type: 'text', options: [], buttonName: '' });
  const [optionInput, setOptionInput] = useState('');

  useEffect(() => {
    getTableStructure().then(setStructure);
    getTableData().then(setData);
  }, []);

  const handleAddColumn = async () => {
    if (!newCol.name) return;
    const updated = [...structure, { ...newCol, id: Date.now() }];
    setStructure(updated);
    await saveTableStructure(updated);
    setNewCol({ name: '', type: 'text', options: [], buttonName: '' });
  };

  // Confirmation state for column delete/edit
  const [confirmDeleteColId, setConfirmDeleteColId] = useState(null);
  const [confirmEditColId, setConfirmEditColId] = useState(null);

  const handleDeleteColumn = async (id) => {
    setConfirmDeleteColId(id);
  };

  // When deleting a column, only remove it from structure, not from row data
  const confirmDeleteColumn = async () => {
    if (confirmDeleteColId == null) return;
    const updated = structure.filter(col => col.id !== confirmDeleteColId);
    setStructure(updated);
    await saveTableStructure(updated);
    setConfirmDeleteColId(null);
    // Do NOT remove the column's data from each row; keep it in row objects
  };


  // --- end Excel Export Logic ---

  // Edit column state
  const [editColIdx, setEditColIdx] = useState(null);
  const handleEditColumn = (id) => {
    const col = structure.find(c => c.id === id);
    if (col) {
      setNewCol({
        name: col.name,
        type: col.type,
        options: col.options || [],
        buttonName: col.buttonName || ''
      });
      setEditColIdx(id);
    }
  };

  const confirmEditColumn = async () => {
    if (editColIdx == null) return;
    const updated = structure.map(col =>
      col.id === editColIdx
        ? { ...col, ...newCol, id: editColIdx }
        : col
    );
    setStructure(updated);
    await saveTableStructure(updated);
    setConfirmEditColId(null);
    setEditColIdx(null);
    setNewCol({ name: '', type: 'text', options: [], buttonName: '' });
  };

  const handleOptionAdd = () => {
    if (!optionInput) return;
    setNewCol({ ...newCol, options: [...newCol.options, optionInput] });
    setOptionInput('');
  };

  // Inline editing state
  const [newRow, setNewRow] = useState({});
  // For tracking which cell is being edited (rowIdx, colId)
  const [editingCell, setEditingCell] = useState({ rowIdx: null, colId: null });
  // For draft edits per row
  const [rowDrafts, setRowDrafts] = useState({});

  // Track which row is being edited
  const [editingRowIdx, setEditingRowIdx] = useState(null);

  // Handle cell change for inline editing (no auto-save)

  // --- Excel Export with Styles ---
  const downloadAsExcel = () => {
    // Define styles
    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "00000000" }, // Black color for text
      },
      fill: {
        fgColor: { rgb: "FFf3f4f6" }, // Background color with full opacity
      },
      border: {
        top: { style: "thin", color: { rgb: "FF0071BD" } },
        bottom: { style: "thin", color: { rgb: "FF0071BD" } },
        left: { style: "thin", color: { rgb: "FF0071BD" } },
        right: { style: "thin", color: { rgb: "FF0071BD" } },
      },
    };

    const cellStyle = {
      border: {
        top: { style: "thin", color: { rgb: "FF0071BD" } },
        bottom: { style: "thin", color: { rgb: "FF0071BD" } },
        left: { style: "thin", color: { rgb: "FF0071BD" } },
        right: { style: "thin", color: { rgb: "FF0071BD" } },
      },
    };

    // Use the current table structure for headers
    const headers = structure.map(col => col.name);
    // Prepare the rows data as currently rendered
    const rows = data.map((row, i) =>
      structure.map(col => {
        // For file columns, show file name if present
        if (col.type === 'file') {
          const colNameKey = col.name.replace(/\s+/g, '').toLowerCase();
          const idKey = col.id;
          const nameKey = colNameKey;
          const idNameKey = `${col.id}_name`;
          const nameNameKey = `${colNameKey}_name`;
          return row[idNameKey] || row[nameNameKey] || row[idKey] || row[nameKey] || '';
        }
        // For other columns, show value by id or name
        const colNameKey = col.name.replace(/\s+/g, '').toLowerCase();
        const idKey = col.id;
        const nameKey = colNameKey;
        const idNameKey = `${col.id}_name`;
        const nameNameKey = `${colNameKey}_name`;
        return row[idKey] ?? row[nameKey] ?? row[idNameKey] ?? row[nameNameKey] ?? '';
      })
    );

    // Prepare data for worksheet
    const worksheetData = [headers, ...rows];

    // Create a worksheet from the data
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Apply styles to the header
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ c: i, r: 0 }); // Header row is at index 0
      if (!worksheet[cellRef]) worksheet[cellRef] = {}; // Initialize cell if not present
      worksheet[cellRef].s = headerStyle; // Apply header style
    }

    // Apply styles to the body cells
    for (let r = 1; r < worksheetData.length; r++) {
      // Start from 1 to skip header row
      for (let c = 0; c < worksheetData[r].length; c++) {
        const cellRef = XLSX.utils.encode_cell({ c, r }); // Get cell reference
        if (!worksheet[cellRef]) worksheet[cellRef] = {}; // Initialize cell if not present
        worksheet[cellRef].s = cellStyle; // Apply cell style
      }
    }

    // Set minimum widths for columns
    worksheet["!cols"] = structure.map(col => {
      // Set width based on type or name
      if (col.type === 'file') return { wch: 16 };
      if (col.type === 'number') return { wch: 8 };
      if (col.type === 'date') return { wch: 10 };
      if (col.name.toLowerCase().includes('email')) return { wch: 24 };
      if (col.name.toLowerCase().includes('mobile')) return { wch: 14 };
      if (col.name.toLowerCase().includes('name')) return { wch: 16 };
      return { wch: 12 };
    });

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Profiles");

    // Generate the Excel file and prompt the user to download it
    const timestamp = new Date().toISOString().split("T")[0]; // Format date as YYYY-MM-DD
    XLSX.writeFile(workbook, `${timestamp}_profiles.xlsx`);
  };
    
  const handleCellChange = (rowIdx, col, value) => {
    setRowDrafts(prev => {
      const prevRow = { ...data[rowIdx], ...(prev[rowIdx] || {}) };
      const nameKey = col.name.replace(/\s+/g, '').toLowerCase();
      return {
        ...prev,
        [rowIdx]: {
          ...prevRow,
          [col.id]: value,
          [nameKey]: value
        }
      };
    });
  };

  // On blur, update the row in data and save to backend
  const handleCellBlur = async (rowIdx) => {
    if (!rowDrafts[rowIdx]) return;
    // For each column, ensure both id and name are present as keys
    const draft = { ...rowDrafts[rowIdx] };
    structure.forEach(col => {
      const nameKey = col.name.replace(/\s+/g, '').toLowerCase();
      if (draft[col.id] !== undefined) {
        draft[nameKey] = draft[col.id];
      }
      if (draft[nameKey] !== undefined) {
        draft[col.id] = draft[nameKey];
      }
    });
    const updated = data.map((row, i) => (i === rowIdx ? draft : row));
    setData(updated);
    setRowDrafts(prev => {
      const copy = { ...prev };
      delete copy[rowIdx];
      return copy;
    });
    await saveTableData(updated);
  };

  // Add new row
  const handleAddRow = async () => {
    if (Object.keys(newRow).length === 0) return;
    // For each column, ensure both id and name are present as keys
    const rowWithNames = { ...newRow };
    structure.forEach(col => {
      const nameKey = col.name.replace(/\s+/g, '').toLowerCase();
      if (rowWithNames[col.id] !== undefined) {
        rowWithNames[nameKey] = rowWithNames[col.id];
      }
      if (rowWithNames[nameKey] !== undefined) {
        rowWithNames[col.id] = rowWithNames[nameKey];
      }
    });
    const updated = [...data, rowWithNames];
    setData(updated);
    setNewRow({});
    await saveTableData(updated);
  };

  // Delete row with confirmation
  const [confirmDeleteRowIdx, setConfirmDeleteRowIdx] = useState(null);
  const handleDeleteRow = (rowIdx) => {
    setConfirmDeleteRowIdx(rowIdx);
  };
  const confirmDeleteRow = async () => {
    if (confirmDeleteRowIdx == null) return;
    const row = data[confirmDeleteRowIdx];
    for (const col of structure) {
      if (col.type === 'file' && row[col.id] && typeof row[col.id] === 'string' && row[col.id].startsWith('http')) {
        await deleteFileByUrl(row[col.id]);
      }
    }
    const updated = data.filter((_, i) => i !== confirmDeleteRowIdx);
    setData(updated);
    await saveTableData(updated);
    setConfirmDeleteRowIdx(null);
  };
  const cancelDeleteRow = () => setConfirmDeleteRowIdx(null);

  // Column widths state for resizing
  const [colWidths, setColWidths] = useState({});
  const resizingCol = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Handle column resize start
  const handleResizeStart = (e, colId) => {
    resizingCol.current = colId;
    startX.current = e.clientX;
    startWidth.current = colWidths[colId] || 160;
    document.addEventListener('mousemove', handleResizing);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  // Handle resizing
  const handleResizing = (e) => {
    if (!resizingCol.current) return;
    const diff = e.clientX - startX.current;
    setColWidths(w => ({ ...w, [resizingCol.current]: Math.max(80, startWidth.current + diff) }));
  };

  // End resizing
  const handleResizeEnd = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', handleResizing);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  // File upload handler for file columns (only for rows with an ID)
  const [uploadingFileIdx, setUploadingFileIdx] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const handleFileUpload = async (file, rowIdx, colId) => {
    if (!file) return;
    setUploadingFileIdx(`${rowIdx}_${colId}`);
    setUploadProgress(0);
    const fileRef = storageRef(storage, `customTableUploads/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);
    uploadTask.on('state_changed',
      (snapshot) => {
        const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploadProgress(percent);
      },
      (error) => {
        setUploadingFileIdx(null);
        setUploadProgress(0);
        alert('File upload failed: ' + error.message);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        // Attach file reference to row under both id and name
        const col = structure.find(c => c.id === colId);
        const nameKey = col ? col.name.replace(/\s+/g, '').toLowerCase() : colId;
        const updated = data.map((row, i) => {
          if (i === rowIdx) {
            return {
              ...row,
              [colId]: url,
              [nameKey]: url,
              [`${colId}_name`]: file.name,
              [`${nameKey}_name`]: file.name
            };
          }
          return row;
        });
        setData(updated);
        await saveTableData(updated);
        setUploadingFileIdx(null);
        setUploadProgress(0);
      }
    );
  };

  // File delete confirmation state
  const [confirmDeleteFile, setConfirmDeleteFile] = useState(null);

  // File delete confirmation handler
  const handleConfirmDeleteFile = async () => {
    if (!confirmDeleteFile) return;
    const { rowIdx, colId, fileUrl } = confirmDeleteFile;
    const col = structure.find(c => c.id === colId);
    const nameKey = col ? col.name.replace(/\s+/g, '').toLowerCase() : colId;
    await deleteFileByUrl(fileUrl);
    const updated = data.map((row, i) => {
      if (i === rowIdx) {
        return {
          ...row,
          [colId]: '',
          [nameKey]: '',
          [`${colId}_name`]: '',
          [`${nameKey}_name`]: ''
        };
      }
      return row;
    });
    setData(updated);
    await saveTableData(updated);
    setConfirmDeleteFile(null);
  };

  // Render cell by type, with placeholder and rounded fields
  const renderCell = (col, value, onChange, isEdit, rowIdx = null, rowObj = null) => {
    // Try to map by id first, then by name (case-insensitive, no spaces), and prefer id if both exist
    const baseClass = "rounded-[6px] border border-gray-300 p-2 focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition w-full min-w-[80px]";
    const placeholder = `Enter ${col.name}`;
    let cellValue = value;
    if (isEdit && rowIdx !== null && rowDrafts[rowIdx]) {
      const colNameKey = col.name.replace(/\s+/g, '').toLowerCase();
      const idKey = col.id;
      const nameKey = colNameKey;
      const idNameKey = `${col.id}_name`;
      const nameNameKey = `${colNameKey}_name`;
      if (rowDrafts[rowIdx][idKey] !== undefined) {
        cellValue = rowDrafts[rowIdx][idKey];
      } else if (rowDrafts[rowIdx][nameKey] !== undefined) {
        cellValue = rowDrafts[rowIdx][nameKey];
      } else if (rowDrafts[rowIdx][idNameKey] !== undefined) {
        cellValue = rowDrafts[rowIdx][idNameKey];
      } else if (rowDrafts[rowIdx][nameNameKey] !== undefined) {
        cellValue = rowDrafts[rowIdx][nameNameKey];
      }
    } else if (rowObj) {
      const colNameKey = col.name.replace(/\s+/g, '').toLowerCase();
      const idKey = col.id;
      const nameKey = colNameKey;
      const idNameKey = `${col.id}_name`;
      const nameNameKey = `${colNameKey}_name`;
      if (rowObj[idKey] !== undefined) {
        cellValue = rowObj[idKey];
      } else if (rowObj[nameKey] !== undefined) {
        cellValue = rowObj[nameKey];
      } else if (rowObj[idNameKey] !== undefined) {
        cellValue = rowObj[idNameKey];
      } else if (rowObj[nameNameKey] !== undefined) {
        cellValue = rowObj[nameNameKey];
      }
    }
    switch (col.type) {
      case 'text':
        return isEdit ? (
          <input
            className={baseClass}
            value={cellValue || ''}
            placeholder={placeholder}
            onChange={e => {
              onChange(e.target.value);
            }}
          />
        ) : (
          cellValue || <span className="text-gray-400 italic">{placeholder}</span>
        );
      case 'number':
        return isEdit ? (
          <input
            type="number"
            className={baseClass}
            value={cellValue || ''}
            placeholder={placeholder}
            onChange={e => {
              onChange(e.target.value);
            }}
          />
        ) : (
          cellValue || <span className="text-gray-400 italic">{placeholder}</span>
        );
      case 'dropdown':
        return isEdit ? (
          <select
            className={baseClass}
            value={cellValue || ''}
            onChange={e => {
              onChange(e.target.value);
            }}
          >
            <option value="">Select {col.name}</option>
            {col.options.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
          </select>
        ) : (
          cellValue || <span className="text-gray-400 italic">Select {col.name}</span>
        );
      case 'checkbox':
        return isEdit ? (
          <input
            type="checkbox"
            className="rounded-[6px] border-gray-300"
            checked={!!cellValue}
            onChange={e => {
              onChange(e.target.checked);
            }}
          />
        ) : (
          cellValue ? 'Yes' : 'No'
        );
      case 'radio':
        return isEdit ? (
          <div className="flex gap-2">{col.options.map((opt, i) => (
            <label key={i} className="rounded-[6px] px-2 py-1 border border-gray-300 cursor-pointer">
              <input
                type="radio"
                name={col.id + '_' + rowIdx}
                checked={cellValue === opt}
                onChange={() => {
                  onChange(opt);
                }}
              /> {opt}
            </label>
          ))}</div>
        ) : (
          cellValue || <span className="text-gray-400 italic">Select {col.name}</span>
        );
      case 'file': {
        let fileUrll = undefined;
        let fileName = "File";
        if (rowObj) {
          const colNameKey = col.name.replace(/\s+/g, '').toLowerCase();
          const idKey = col.id;
          const nameKey = colNameKey;
          const idNameKey = `${col.id}_name`;
          const nameNameKey = `${colNameKey}_name`;
          fileUrll = rowObj[idKey] || rowObj[nameKey] || rowObj[idNameKey] || rowObj[nameNameKey];
          fileName = rowObj[idNameKey] || rowObj[nameNameKey] || rowObj[idKey] || rowObj[nameKey] || "File";
        }
        if (!isEdit && !fileUrll) {
          return (
            <div className="flex items-center gap-2">
              <button
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 border border-gray-300 rounded-lg cursor-not-allowed bg-gray-100 w-full"
                disabled
              >
                <Upload className="w-4 h-4" />
                <span>Upload File</span>
              </button>
            </div>
          );
        }
        if (fileUrll) {
          return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-[#0071bd] rounded-lg w-full">
              <a
                href={fileUrll}
                download={fileName}
                className="text-[#0071bd] hover:text-blue-700 p-0.5 rounded-full hover:bg-blue-100"
                title="Download File"
              >
                <Download className="w-4 h-4" />
              </a>
              <span className="flex-1 truncate text-sm">{fileName}</span>
              {isEdit && (
                <button
                  onClick={() => setConfirmDeleteFile({ rowIdx, colId: col.id, fileUrl: fileUrll, colName: col.name })}
                  className="text-red-600 hover:text-red-900 p-0.5 rounded-full hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        }
        return (
          <>
            <label className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#0071bd] border border-[#0071bd] rounded-lg hover:bg-[#0071bd] hover:text-white transition-colors w-full cursor-pointer">
              <Upload className="w-4 h-4" />
              <span>Upload File</span>
              <input
                type="file"
                className="hidden"
                onChange={e => handleFileUpload(e.target.files[0], rowIdx, col.id)}
                disabled={uploadingFileIdx === `${rowIdx}_${col.id}`}
                accept=".pdf,.doc,.docx"
              />
            </label>
            {uploadingFileIdx === `${rowIdx}_${col.id}` && (
              <div className="text-xs text-blue-700 mt-1">Uploading... {uploadProgress}%</div>
            )}
          </>
        );
      }
      case 'button':
        return (
          <button className="bg-blue-500 text-white px-2 py-1 rounded-[6px]">{col.buttonName || 'Button'}</button>
        );
      case 'date':
        return isEdit ? (
          <input
            type="date"
            className={baseClass}
            value={value || ''}
            onChange={e => {
              onChange(e.target.value);
              if (rowIdx !== null) handleCellBlur(rowIdx);
            }}
          />
        ) : (
          value || <span className="text-gray-400 italic">{placeholder}</span>
        );
      default:
        return value || <span className="text-gray-400 italic">{placeholder}</span>;
    }
  };

  // --- Edit column popup logic ---
  const [editColDraft, setEditColDraft] = useState(null);
  const handleEditColDraftChange = (field, value) => {
    setEditColDraft({ ...editColDraft, [field]: value });
  };
  {
    confirmEditColId !== null && editColDraft && (
      <ConfirmationPopup
        message="Edit column details:"
        name={editColDraft.name}
        onConfirm={confirmEditColumn}
        onCancel={() => { setConfirmEditColId(null); setEditColDraft(null); }}
        confirmLabel="Save"
        cancelLabel="Cancel"
      >
        <div className="flex flex-col gap-2 mt-2">
          <input
            className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition"
            value={editColDraft.name}
            onChange={e => handleEditColDraftChange('name', e.target.value)}
            placeholder="Column Name"
          />
          <select
            className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition"
            value={editColDraft.type}
            onChange={e => handleEditColDraftChange('type', e.target.value)}
          >
            {inputTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </ConfirmationPopup>
    )
  }
  // --- end edit column popup ---

  // --- Excel Export Button (header) ---
  <div className="flex justify-between items-center mb-4">
    <h2 className="text-xl font-semibold text-[#0078D4]">Profiles</h2>
    <button
      onClick={downloadAsExcel}
      disabled={data.length === 0}
      className="flex items-center justify-center p-2 border border-green-500 bg-green-100 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-green-200 active:scale-95"
    >
      <Download className="w-4 h-4 mr-2 transition-transform duration-300 ease-in-out" />
      <span className="hidden sm:inline">Download Excel</span>
    </button>
  </div>
  // --- end Excel Export Button ---

  // --- end Excel Export Logic ---

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6 gap-4 bg-gray-100 p-3 rounded-md shadow-md">
        <h2 className="text-2xl font-bold text-[#0071bd] m-0">Customizable Experienced Profiles Table</h2>
        <button
          onClick={downloadAsExcel}
          disabled={data.length === 0}
          className="flex items-center justify-center p-2 border border-green-500 bg-green-100 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-green-200 active:scale-95"
        >
          <Download className="w-4 h-4 mr-2 transition-transform duration-300 ease-in-out" />
          <ExcelIcon className="h-6 w-6 transition-transform duration-300 ease-in-out hidden sm:flex" />
          <span className="mx-2" />
        </button>
      </div>
     
      <div className="mb-6 flex flex-wrap gap-3 items-end bg-gray-50 p-4 rounded-md shadow">
        <input
          className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition w-48"
          placeholder="Column Name"
          value={newCol.name}
          onChange={e => setNewCol({ ...newCol, name: e.target.value })}
        />
        <select
          className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition w-40"
          value={newCol.type}
          onChange={e => setNewCol({ ...newCol, type: e.target.value, options: [], buttonName: '' })}
        >
          {inputTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {['dropdown', 'radio'].includes(newCol.type) && (
          <>
            <input
              className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition w-40"
              placeholder="Option"
              value={optionInput}
              onChange={e => setOptionInput(e.target.value)}
            />
            <button className="bg-[#0071bd] text-white px-3 py-2 rounded shadow hover:bg-blue-700 transition" onClick={handleOptionAdd}>Add Option</button>
            <div className="flex gap-1 flex-wrap">
              {newCol.options.map((opt, i) => (
                <span key={i} className="bg-gray-200 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                  {opt}
                  <button
                    type="button"
                    className="ml-1 p-0.5 rounded-full hover:bg-red-100"
                    title="Remove option"
                    onClick={() => {
                      setNewCol({
                        ...newCol,
                        options: newCol.options.filter((_, idx) => idx !== i)
                      });
                    }}
                  >
                    <X className="w-3 h-3 text-red-500" strokeWidth={2.5} />
                  </button>
                </span>
              ))}
            </div>
          </>
        )}
        {newCol.type === 'button' && (
          <input
            className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-[#0071bd] focus:border-[#0071bd] transition w-40"
            placeholder="Button Name"
            value={newCol.buttonName}
            onChange={e => setNewCol({ ...newCol, buttonName: e.target.value })}
          />
        )}
        {editColIdx == null ? (
          <button className="bg-[#0071bd] text-white px-4 py-2 rounded shadow hover:bg-[#005fa3] transition flex items-center gap-2" onClick={handleAddColumn}>
            <Plus className="w-4 h-4 mr-1" /> Add Column
          </button>
        ) : (
          <button className="bg-yellow-500 text-white px-4 py-2 rounded shadow hover:bg-yellow-600 transition flex items-center gap-2" onClick={confirmEditColumn}>
            <Pencil className="w-4 h-4 mr-1" /> Update Column
          </button>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg shadow">
        <table className="min-w-full bg-white rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-[#0071bd] text-white select-none">
              {structure.map(col => (
                <th
                  key={col.id}
                  className="px-4 py-3 text-left font-semibold text-sm tracking-wider border-b border-blue-200 relative group"
                  style={{ width: colWidths[col.id] || 160, minWidth: 80 }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>{col.name}</span>
                    <div className="flex gap-1">
                      <button
                        className="bg-transparent p-1 rounded hover:bg-[#0071bd] transition flex items-center"
                        title="Edit Column"
                        onClick={() => handleEditColumn(col.id)}
                      >
                        <Pencil className="w-4 h-4 text-white hover:scale-125" strokeWidth={2} />
                      </button>
                      <button
                        className="bg-transparent p-1 rounded hover:bg-red-100 transition flex items-center group"
                        title="Delete Column"
                        onClick={() => handleDeleteColumn(col.id)}
                      >
                        <Trash2 className="w-4 h-4 text-white group-hover:text-red-600 group-hover:stroke-2 hover:scale-125 group-hover:rounded" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                  {/* Resize handle */}
                  <div
                    className="absolute top-0 right-0 h-full w-2 cursor-col-resize group-hover:bg-blue-300/30"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleResizeStart(e, col.id);
                    }}
                    style={{ zIndex: 10 }}
                  />
                </th>
              ))}
              <th className="px-4 py-3 text-left font-semibold text-sm tracking-wider border-b border-blue-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                {structure.map(col => {
                  // Always resolve value by id, normalized name, or _name variants
                  const colNameKey = col.name.replace(/\s+/g, '').toLowerCase();
                  const idKey = col.id;
                  const nameKey = colNameKey;
                  const idNameKey = `${col.id}_name`;
                  const nameNameKey = `${colNameKey}_name`;
                  let cellValue = undefined;
                  if (row[idKey] !== undefined) {
                    cellValue = row[idKey];
                  } else if (row[nameKey] !== undefined) {
                    cellValue = row[nameKey];
                  } else if (row[idNameKey] !== undefined) {
                    cellValue = row[idNameKey];
                  } else if (row[nameNameKey] !== undefined) {
                    cellValue = row[nameNameKey];
                  }
                  // Only allow editing if this row is being edited
                  const isEdit = editingRowIdx === rowIdx;
                  return (
                    <td key={col.id} className="px-4 py-2 border-b border-gray-200">
                      {renderCell(
                        col,
                        rowDrafts[rowIdx]?.[col.id] ?? cellValue ?? '',
                        val => handleCellChange(rowIdx, col, val),
                        isEdit,
                        rowIdx,
                        row
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-2 border-b border-gray-200 flex gap-2 items-center">
                  {editingRowIdx === rowIdx ? (
                    <button
                      className="border border-green-500 text-green-600 rounded-full p-1 transition-transform duration-150 hover:scale-110 hover:bg-green-50"
                      onClick={() => {
                        handleCellBlur(rowIdx);
                        setEditingRowIdx(null);
                      }}
                      title="Save"
                    >
                      {/* Lucide-react check icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </button>
                  ) : (
                    <button
                      className="border border-blue-500 text-blue-600 rounded-full p-1 transition-transform duration-150 hover:scale-110 hover:bg-blue-50"
                      onClick={() => setEditingRowIdx(rowIdx)}
                      title="Edit"
                    >
                      {/* Lucide-react Pencil icon */}
                      <Pencil className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    className="border border-red-500 text-red-500 rounded-full p-1 transition-transform duration-150 hover:scale-110 hover:bg-red-50"
                    onClick={() => handleDeleteRow(rowIdx)}
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </td>
                {/* Confirmation Popup for Delete Row */}
                {confirmDeleteRowIdx !== null && (
                  <ConfirmationPopup
                    message="Are you sure you want to delete this record?"
                    onConfirm={confirmDeleteRow}
                    onCancel={cancelDeleteRow}
                  />
                )}
              </tr>
            ))}
            {/* New row input (file upload removed for new rows) */}
            <tr className="bg-blue-50">
              {structure.map(col => {
                // Always resolve value by id or normalized name for newRow
                const colNameKey = col.name.replace(/\s+/g, '').toLowerCase();
                let cellValue = undefined;
                if (newRow[col.id] !== undefined) {
                  cellValue = newRow[col.id];
                } else {
                  const foundKey = Object.keys(newRow).find(k => k.replace(/\s+/g, '').toLowerCase() === colNameKey);
                  if (foundKey !== undefined) {
                    cellValue = newRow[foundKey];
                  }
                }
                return (
                  <td key={col.id} className="px-4 py-2 border-b border-blue-200">
                    {col.type === 'file'
                      ? <div className="flex items-center gap-2"><button className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 border border-gray-300 rounded-lg cursor-not-allowed bg-gray-100 w-full" disabled><Upload className="w-4 h-4" /><span>Upload File</span></button></div>
                      : renderCell(col, cellValue, val => setNewRow({ ...newRow, [col.id]: val, [colNameKey]: val }), true, null, newRow)}
                  </td>
                );
              })}
              <td className="px-4 py-2 border-b border-blue-200">
                <button className="bg-[#0071bd] text-white p-2 rounded-full shadow hover:bg-[#005fa3] transition flex items-center justify-center" onClick={handleAddRow} title="Add Row">
                  <Plus className="w-5 h-5" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Confirmation Popup for Delete Column */}
      {confirmDeleteColId !== null && (
        <ConfirmationPopup
          message="Are you sure you want to delete this column?"
          name={structure.find(col => col.id === confirmDeleteColId)?.name || ''}
          onConfirm={confirmDeleteColumn}
          onCancel={() => setConfirmDeleteColId(null)}
        />
      )}
      {/* File Delete Confirmation Popup (for file cells) */}
      {confirmDeleteFile && (
        <ConfirmationPopup
          message="Are you sure you want to delete this file?"
          name={confirmDeleteFile.colName || ''}
          onConfirm={handleConfirmDeleteFile}
          onCancel={() => setConfirmDeleteFile(null)}
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
    </div>
  );
};
